import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Plane, ArrowRight, Loader2 } from "lucide-react";
import { ConsentCheckbox } from "@/components/ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  { key: "destination_city", label: "Destination city (AUS)", chips: ["Melbourne", "Sydney", "Brisbane", "Gold Coast", "Perth", "Adelaide", "Not sure"] },
  { key: "family_situation", label: "Family situation", chips: ["Just me", "Partner", "Partner + kids", "Single parent"] },
  { key: "pets", label: "Pets?", chips: ["No pets", "Yes — dog", "Yes — cat", "Yes — other"] },
  { key: "move_timeline", label: "Move timeline", chips: ["Within 3 months", "3–6 months", "6–12 months", "Just exploring"] },
  { key: "rent_budget", label: "Monthly budget for rent (AUD)", chips: ["Under $1,500", "$1,500–$2,500", "$2,500–$3,500", "$3,500+"] },
  { key: "biggest_concern", label: "Biggest concern", chips: ["Finding a job", "Finding a rental", "Tax and admin", "Moving with family", "Cost of living", "Pets"] },
  { key: "employment_status", label: "Employed?", chips: ["Yes, job secured", "Actively looking", "Not started yet"] },
];

const inputCls =
  "w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors";
const labelCls = "block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-2";

export default function Questionnaire() {
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    email: params.get("email") || "",
    first_name: params.get("first_name") || "",
    current_city: "",
    destination_city: "",
    job_type: "",
    family_situation: "",
    pets: "",
    move_timeline: "",
    rent_budget: "",
    biggest_concern: "",
    employment_status: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = Object.entries(form).filter(([, v]) => !String(v).trim());
    if (missing.length) {
      toast.error("Please answer every question — it all feeds your call with the team.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the acknowledgement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire`, form);
      setDone(true);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex flex-col" data-testid="questionnaire-success">
        <header className="px-5 md:px-8 py-5">
          <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
            <Plane size={18} /> The Ditch Collective
          </a>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 pb-20">
          <div className="w-full max-w-lg bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl">
            <CheckCircle2 size={44} className="mx-auto text-[var(--lime-deep)]" />
            <h1 className="font-display text-3xl font-extrabold mt-6">All set!</h1>
            <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
              The team will have your answers on hand for your call — so you can skip the basics and
              get straight into the good stuff.
            </p>
            <a href="/" className="mt-7 inline-block btn-ghost text-sm">← Back to the site</a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="questionnaire-page">
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="max-w-2xl mx-auto px-4 pb-24">
        <span className="num-tag text-xs">Pre-call questionnaire</span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2 leading-tight">
          Help the team prep <em className="font-serif">your</em> call.
        </h1>
        <p className="text-[var(--ocean)] mt-3 mb-10">
          Two minutes, nine questions — so your 30-minute call is all about answers, not admin.
        </p>

        <form onSubmit={submit} className="space-y-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First name</label>
              <input data-testid="questionnaire-first-name" className={inputCls} value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)} placeholder="Your first name" />
            </div>
            <div>
              <label className={labelCls}>Email — the one you booked with</label>
              <input data-testid="questionnaire-email" type="email" className={inputCls} value={form.email}
                onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Current city (NZ)</label>
            <input data-testid="questionnaire-current-city" className={inputCls} value={form.current_city}
              onChange={(e) => set("current_city", e.target.value)} placeholder="e.g. Auckland, Tauranga, Christchurch" />
          </div>

          <div>
            <label className={labelCls}>Job type / industry</label>
            <input data-testid="questionnaire-job-type" className={inputCls} value={form.job_type}
              onChange={(e) => set("job_type", e.target.value)} placeholder="e.g. Registered nurse, builder, marketing" />
          </div>

          {QUESTIONS.map((qd) => (
            <div key={qd.key}>
              <label className={labelCls}>{qd.label}</label>
              <div className="flex flex-wrap gap-2" data-testid={`questionnaire-${qd.key.replace(/_/g, "-")}`}>
                {qd.chips.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="chip"
                    data-active={form[qd.key] === c}
                    data-testid={`questionnaire-${qd.key.replace(/_/g, "-")}-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => set(qd.key, c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <ConsentCheckbox checked={consent} onChange={setConsent} testId="questionnaire-consent" />

          <button
            type="submit"
            data-testid="questionnaire-submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-4 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : (<>Send to the team <ArrowRight size={16} /></>)}
          </button>
        </form>
      </main>
    </div>
  );
}
